import React, { useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import useToast from "@/hooks/useToast";
import { UserType } from "@/types/db/users";
import { theme } from "@/constants/themes";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { ShiftInput } from "@/components/inputs/ShiftInput";
import { UserInput } from "@/components/inputs/UserInput";
import { PositionInput } from "@/components/inputs/PositionInput";
import { shiftsService } from "@/services/shiftService";
import { type LookupInputOption } from "@/components/ui/LookupInput";
import useAuth from "@/hooks/useAuth";
import { EmbeddedTimeEditor } from "@/components/Editors/TimeEditor/EmbeddedTimeEditor";
import type { TimeData } from "@/components/Editors/TimeEditor";
import { getTimecardStatusOptions } from "@/config/tableConfigs/timecardTableConfig";

export function getTimecardStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    APPROVED: colors.complete.border,
    PENDING: colors.pending.border,
    REJECTED: colors.attention.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

// Re-export for backwards compatibility
export { getTimecardStatusOptions };

type BillingType = "BILLABLE" | "NON_BILLABLE";
type StatusType = "PENDING" | "APPROVED" | "REJECTED";

export type TimecardForm = {
  created_by: string;
  shift_id?: string | null;
  shift_user_id: string;
  user_id: string;
  client_id?: string | null;
  project_id?: string | null;
  shift_position_id?: string | null;
  time: TimeData | null;
  status: StatusType;
  billing: BillingType;
  meta_data: {
    note?: string;
    position_snapshot?: any;
    user_pay_snapshot?: any;
  };
};

export type TimecardPayload = { timecard: TimecardForm };

type TimecardCreateFormProps = {
  initialTimecard?: Partial<TimecardForm>;
  onSave?: (payload: TimecardPayload) => Promise<void> | void;
  onChange?: (payload: TimecardPayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onStatusChange?: (status: string) => void;
  onUserChange?: (user: UserType | null) => void;
};

type UserPositionOption = LookupInputOption & {
  shiftId: string;
};

type UserShiftOption = LookupInputOption & {
  projectId: string;
};

export interface TimecardCreateFormRef {
  handleSave: () => Promise<void>;
  handleReset: () => void;
  updateUserId: (userId: string) => void;
  //updateDate: (date: string) => void;
  updateStatus: (status: StatusType) => void;
  getStatus: () => StatusType;
}

export const TimecardCreateForm = forwardRef<TimecardCreateFormRef, TimecardCreateFormProps>(
  ({ initialTimecard, onSave, onChange, onDirtyChange, onStatusChange }, ref) => {
    const toast = useToast();
    const { user, displayNames } = useAuth();

    const buildInitial = (): TimecardForm => {
      const base: TimecardForm = {
        created_by: initialTimecard?.created_by ?? user?.id ?? "",
        shift_id: initialTimecard?.shift_id ?? null,
        shift_user_id: initialTimecard?.shift_user_id ?? initialTimecard?.user_id ?? "",
        user_id: initialTimecard?.user_id ?? "",
        client_id: initialTimecard?.client_id ?? null,
        project_id: initialTimecard?.project_id ?? null,
        shift_position_id: initialTimecard?.shift_position_id ?? null,
        time: initialTimecard?.time ?? null,
        status: (initialTimecard?.status as StatusType) ?? "PENDING",
        billing: (initialTimecard?.billing as BillingType) ?? "BILLABLE",
        meta_data: {
          ...(initialTimecard?.meta_data ?? {}),
        },
      };

      return base;
    };

    const [timecard, setTimecard] = useState<TimecardForm>(buildInitial);
    const [loading, setLoading] = useState(false);
    const [projectOptions, setProjectOptions] = useState<LookupInputOption[]>();
    const [userShiftOptions, setUserShiftOptions] = useState<UserShiftOption[]>([]);
    const [userPositionOptions, setUserPositionOptions] = useState<UserPositionOption[]>([]);
    // Map of shift_id → shift_position_id for the selected user
    const userPositionMap = useRef<Map<string, string>>(new Map());

    const baseline = useRef(JSON.stringify({ timecard: buildInitial() }));

    const dirty = useMemo(() => {
      const isDirty = JSON.stringify({ timecard }) !== baseline.current;
      onDirtyChange?.(isDirty);
      return isDirty;
    }, [timecard, onDirtyChange]);

    const updateTimecard = (updates: Partial<TimecardForm>) => {
      setTimecard((prev) => {
        const next = { ...prev, ...updates };

        onChange?.({ timecard: next });

        if (updates.status !== undefined && updates.status !== prev.status) {
          onStatusChange?.(updates.status);
        }

        return next;
      });
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      handleSave,
      handleReset: () => {
        const nextTimecard = buildInitial();
        setTimecard(nextTimecard);
        baseline.current = JSON.stringify({ timecard: nextTimecard });
        onStatusChange?.(nextTimecard.status);
      },
      updateUserId: (userId: string) => {
        updateTimecard({ user_id: userId, shift_user_id: userId });
      },
      updateStatus: (status: StatusType) => updateTimecard({ status }),
      getStatus: () => timecard.status,
    }));

    const handleSave = async () => {
      if (!timecard.user_id.trim()) {
        toast.showToast({ title: "Missing User", message: "Please select a user", toastType: "warning" });
        return;
      }

      if (!timecard.time?.start_time) {
        toast.showToast({ title: "Missing Time", message: "Please set time details", toastType: "warning" });
        return;
      }

      if (!timecard.time.segments?.length) {
        toast.showToast({
          title: "Missing Segments",
          message: "Please add at least one time segment",
          toastType: "warning",
        });
        return;
      }

      if (!timecard.project_id) {
        toast.showToast({ title: "Missing Project", message: "Please select a project", toastType: "warning" });
        return;
      }

      if (!timecard.shift_id) {
        toast.showToast({ title: "Missing Shift", message: "Please select a shift", toastType: "warning" });
        return;
      }

      if (!timecard.shift_position_id) {
        toast.showToast({ title: "Missing Position", message: "Please select a position", toastType: "warning" });
        return;
      }

      setLoading(true);
      try {
        await onSave?.({ timecard });
      } finally {
        setLoading(false);
      }
    };

    const handleUserChange = async (userId: string) => {
      if (!userId) return;

      updateTimecard({ project_id: "", shift_id: null, shift_position_id: null, client_id: null });
      userPositionMap.current.clear();

      const userShifts = await shiftsService.getShiftsByUserId(userId, false);
      const pastShifts = userShifts.filter((shift) => new Date(shift.end_time ?? "").getTime() < Date.now());

      // Build shift_id → shift_position_id map by finding the position the user is assigned to
      const scheduledShifts = pastShifts.filter((shift) => {
        const userPosition = shift.shift_positions?.find((sp: any) =>
          sp.shift_users?.some((su: any) => su.id === userId && su.status !== "INVITED" && su.status !== "QUEUED"),
        );
        if (userPosition) {
          userPositionMap.current.set(shift.id!, userPosition.id as string);
          return true;
        }
        return false;
      });

      const nextOptions = scheduledShifts.map((p) => {
        const project = p.project;

        return {
          id: project?.id!,
          name: project?.name,
          subtitle: project?.client?.name ?? undefined,
          icon: "folder" as const,
          // badge: project?.status
          //   ? {
          //     text: project?.status,
          //     tone: project?.status === "IN PROGRESS" ? ("emerald" as const) : ("amber" as const),
          //   }
          //   : undefined,
        };
      });

      const uniqueOptions = Array.from(new Map(nextOptions.map((o) => [o.id, o])).values());

      // Build shift options for the LookupInput, tagged with projectId for filtering
      const shiftOpts = scheduledShifts.map((s) => {
        const dateStr = s.start_time
          ? new Date(s.start_time).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
          : "";
        return {
          id: s.id!,
          projectId: s.project?.id ?? "",
          name: s.name || `Shift ${dateStr}`,
          subtitle: dateStr,
          icon: "calendar-clock" as const,
          badge: s.status
            ? {
                text: s.status,
                tone: (s.status === "COMPLETE" || s.status === "APPROVED"
                  ? "emerald"
                  : s.status === "PENDING"
                    ? "amber"
                    : "slate") as "emerald" | "amber" | "slate",
              }
            : undefined,
        };
      });

      // Build position options from the user's assigned positions, tagged with shiftId
      const positionOpts = scheduledShifts.flatMap((s) => {
        const userPosition = s.shift_positions?.find((sp: any) => sp.shift_users?.some((su: any) => su.id === userId));
        if (!userPosition?.position) return [];
        return [
          {
            id: userPosition.id,
            shiftId: s.id!,
            name: userPosition.position.name,
            subtitle: userPosition.position.description ?? undefined,
            icon: "badge-account-horizontal" as const,
          },
        ];
      });

      setProjectOptions(uniqueOptions as LookupInputOption[]);
      setUserShiftOptions(shiftOpts);
      setUserPositionOptions(positionOpts as UserPositionOption[]);
      updateTimecard({ user_id: userId, shift_user_id: userId });
    };

    const handleShiftChange = async (shiftId: string) => {
      if (!shiftId) return;

      const shift = await shiftsService.getShiftById(shiftId);

      if (!shift) {
        toast.showToast({
          title: "Shift not found",
          message: "Couldn't find shift.",
          toastType: "warning",
        });
        return;
      }

      // Look up the user's shift_position_id from the map on user select
      const shiftPositionId = userPositionMap.current.get(shiftId) ?? null;

      // Build TimeData with a worked segment spanning the full shift duration
      const startMs = new Date(shift.start_time as string).getTime();
      const endMs = new Date(shift.end_time as string).getTime();
      const durationHours = (endMs - startMs) / (1000 * 60 * 60);

      const shiftTimeData: TimeData = {
        start_time: shift.start_time as string,
        end_time: shift.end_time as string,
        segments:
          durationHours > 0
            ? [
                {
                  id: crypto.randomUUID(),
                  type: "worked",
                  duration: durationHours,
                  billable: true,
                  payable: true,
                },
              ]
            : [],
      };

      updateTimecard({
        shift_id: shift.id,
        client_id: shift.client_id ?? null,
        project_id: shift.project_id ?? null,
        shift_position_id: shiftPositionId,
        time: shiftTimeData,
      });
    };

    // Filter shift options by the currently selected project
    const filteredShiftOptions = useMemo(() => {
      if (!timecard.project_id) return [];
      return userShiftOptions.filter((o) => o.projectId === timecard.project_id);
    }, [userShiftOptions, timecard.project_id]);

    // Filter position options by the currently selected shift
    const filteredPositionOptions = useMemo(() => {
      if (!timecard.shift_id) return [];
      return userPositionOptions.filter((o) => o.shiftId === timecard.shift_id);
    }, [userPositionOptions, timecard.shift_id]);

    return (
      <View style={{ flex: 1, gap: 16 }}>
        <UserInput
          value={timecard.user_id || null}
          label="Crew Member"
          onChange={(userId) => handleUserChange(userId || "")}
        />

        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>
            {displayNames?.projects?.name} & {displayNames?.shifts?.name}
          </ThemedText>

          <ProjectInput
            value={timecard.project_id || null}
            label={displayNames?.projects?.name || "Project"}
            optionsSource={projectOptions}
            onChange={(projectId) => updateTimecard({ project_id: projectId })}
          />

          <ShiftInput
            label={displayNames?.shifts.name || "Shift"}
            value={timecard.shift_id || null}
            projectId={timecard.project_id || null}
            optionsSource={filteredShiftOptions}
            placeholder="Select a shift"
            onChange={(id) => handleShiftChange(id || "")}
          />

          <PositionInput
            value={timecard.shift_position_id || null}
            optionsSource={filteredPositionOptions}
            onChange={(positionId) => updateTimecard({ shift_position_id: positionId })}
          />
        </View>

        <View style={styles.timeSection}>
          <ThemedText style={styles.sectionTitle}>Time Details</ThemedText>

          <EmbeddedTimeEditor
            value={timecard.time}
            hideTitle
            onSave={(data: TimeData) => {
              updateTimecard({ time: data });
            }}
            onCancel={() => {}}
          />
        </View>

        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Additional Information</ThemedText>

          <ThemedInput
            testID="timecard-note-input"
            label="Note"
            value={timecard.meta_data.note || ""}
            onChangeText={(text) =>
              updateTimecard({
                meta_data: { ...timecard.meta_data, note: text },
              })
            }
            placeholder="Enter notes"
            multiline
            numberOfLines={4}
            style={styles.textArea}
          />
        </View>
      </View>
    );
  },
);

TimecardCreateForm.displayName = "TimecardCreateForm";

const styles = StyleSheet.create({
  section: {
    marginBottom: 24,
  },
  timeSection: {
    marginBottom: 24,
    zIndex: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 12,
  },
  textArea: {
    marginBottom: 12,
    minHeight: 80,
  },
});
